import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  XCircle,
  Timer,
  ArrowRight,
  Sparkles,
  Loader2,
  DoorOpen,
  Trophy,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  EXAM_LABELS,
  SUBJECT_LABELS,
  DIFFICULTY_LABELS,
  difficultyClass,
  subjectAccent,
  fmtTime,
  arraysEqualSet,
} from "@/lib/pyqMeta";
import { MathText } from "@/components/MathText";
import { QuestionContent } from "@/components/QuestionContent";
import { BookmarkButton } from "@/components/BookmarkButton";
import { SolutionVideo } from "@/components/SolutionVideo";
import { QuestionFeedback } from "@/components/QuestionFeedback";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const TIME_LIMIT = 120; // seconds per question in TIMED mode

const LETTERS = { a: "A", b: "B", c: "C", d: "D" };

export default function Session() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get("mode") || "UNTIMED";

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [bookmarks, setBookmarks] = useState(new Set());

  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [wasCorrect, setWasCorrect] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);

  const [tally, setTally] = useState({ correct: 0, incorrect: 0 });
  const [elapsed, setElapsed] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [finished, setFinished] = useState(false);

  const submittedRef = useRef(false);
  const startedRef = useRef(false);

  // Load questions + create session
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const bookmarked = params.get("bookmarked");
    const mistakes = params.get("mistakes");
    const q = {};
    ["exam", "subject", "chapter", "difficulty", "yearFrom", "yearTo", "search", "source"].forEach((k) => {
      const v = params.get(k);
      if (v) q[k === "exam" ? "exam" : k] = v;
    });
    q.limit = 20;
    const folder = params.get("folder");
    (async () => {
      try {
        let listPromise;
        let filters;
        if (mistakes) {
          listPromise = api.get("/mistakes");
          filters = { mistakes: true };
        } else if (bookmarked) {
          listPromise = api.get("/bookmarks", { params: folder ? { folder } : {} });
          filters = { bookmarked: true, ...(folder ? { folder } : {}) };
        } else {
          listPromise = api.get("/pyq", { params: q });
          filters = q;
        }
        const [listRes, sesRes] = await Promise.all([
          listPromise,
          api.post("/sessions", { mode, filters }),
        ]);
        let list = listRes.data.items;
        // client-side subject filter for bookmarked/mistakes views
        const subj = params.get("subject");
        if ((bookmarked || mistakes) && subj) list = list.filter((x) => x.subject === subj);
        setQuestions(list);
        setSessionId(sesRes.data.id);
        api.get("/bookmarks/ids").then((r) => setBookmarks(new Set(r.data))).catch(() => {});
      } catch {
        toast.error("Could not start session");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBookmark = (id, on) =>
    setBookmarks((prev) => {
      const n = new Set(prev);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  const current = questions[idx];
  const correctSet = useMemo(() => {
    if (!current) return [];
    return Array.isArray(current.correctAnswer) ? current.correctAnswer : [current.correctAnswer];
  }, [current]);
  const isMultiple = current?.questionType === "MCQ_MULTIPLE";

  // Timer
  useEffect(() => {
    if (loading || finished || !current) return;
    const t = setInterval(() => {
      setElapsed((e) => e + 1);
      setTotalTime((t2) => t2 + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [loading, finished, current]);

  // Auto submit in timed mode
  useEffect(() => {
    if (mode === "TIMED" && !submittedRef.current && elapsed >= TIME_LIMIT && current) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed]);

  const toggleOption = (id) => {
    if (submitted) return;
    if (isMultiple) {
      setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    } else {
      setSelected([id]);
    }
  };

  const handleSubmit = async () => {
    if (submittedRef.current || !current) return;
    if (selected.length === 0 && elapsed < TIME_LIMIT) {
      toast.warning("Select an answer first");
      return;
    }
    submittedRef.current = true;
    const correct = isMultiple
      ? arraysEqualSet(selected, correctSet)
      : selected[0] === correctSet[0];
    setWasCorrect(correct);
    setSubmitted(true);
    setTally((t) => ({
      correct: t.correct + (correct ? 1 : 0),
      incorrect: t.incorrect + (correct ? 0 : 1),
    }));
    try {
      await api.post("/attempts", {
        questionId: current.id,
        sessionId,
        selectedAnswer: isMultiple ? selected : selected[0] || "",
        isCorrect: correct,
        timeSpentSec: elapsed,
      });
    } catch {
      /* non-blocking */
    }
  };

  const finishSession = async (finalTally) => {
    setFinished(true);
    try {
      await api.put(`/sessions/${sessionId}/complete`, {
        total: finalTally.correct + finalTally.incorrect,
        correct: finalTally.correct,
        incorrect: finalTally.incorrect,
        durationSec: totalTime,
      });
    } catch {
      /* non-blocking */
    }
  };

  const handleNext = () => {
    if (idx + 1 >= questions.length) {
      finishSession(tally);
      return;
    }
    setIdx((i) => i + 1);
    setSelected([]);
    setSubmitted(false);
    submittedRef.current = false;
    setWasCorrect(false);
    setElapsed(0);
  };

  const handleExit = () => {
    if (window.confirm("Exit this session? Your finished questions will be saved.")) {
      finishSession(tally);
    }
  };

  const regenerate = async () => {
    if (!current) return;
    setRegenLoading(true);
    try {
      const res = await api.post(`/pyq/${current.id}/regenerate-solution`);
      setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, solution: res.data.solution } : q)));
      toast.success("Fresh AI solution generated");
    } catch {
      toast.error("AI generation failed. Try again.");
    } finally {
      setRegenLoading(false);
    }
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Skeleton className="mb-6 h-6 w-40" />
        <Skeleton className="mb-4 h-40 w-full rounded-2xl" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!loading && questions.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="font-heading text-2xl font-bold">No questions to practise</p>
        <p className="mt-2 text-muted-foreground">Adjust your filters and try again.</p>
        <Button asChild className="mt-6 rounded-full px-6">
          <Link to="/student/practice/pyq">Back to filters</Link>
        </Button>
      </div>
    );
  }

  // ---------- Summary ----------
  if (finished) {
    const total = tally.correct + tally.incorrect;
    const acc = total ? Math.round((tally.correct / total) * 100) : 0;
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          data-testid="session-summary"
          className="rounded-3xl border border-border bg-card p-8 text-center sm:p-12"
        >
          <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Trophy className="h-8 w-8" />
          </span>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Session complete</h1>
          <p className="mt-2 text-muted-foreground">Nice work. Here's how you did.</p>

          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="rounded-2xl border border-border bg-secondary/40 p-5">
              <div className="font-heading text-3xl font-bold" data-testid="summary-accuracy">{acc}%</div>
              <div className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">Accuracy</div>
            </div>
            <div className="rounded-2xl border border-border bg-secondary/40 p-5">
              <div className="font-heading text-3xl font-bold text-green-600 dark:text-green-400">{tally.correct}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">Correct</div>
            </div>
            <div className="rounded-2xl border border-border bg-secondary/40 p-5">
              <div className="font-heading text-3xl font-bold text-red-500">{tally.incorrect}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">Wrong</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            {total} attempted · {fmtTime(totalTime)} total time
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild className="rounded-full px-6" data-testid="summary-stats-btn">
              <Link to="/student/performance">View my stats</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-6" data-testid="summary-again-btn">
              <Link to="/student/practice">
                <RotateCcw className="mr-1.5 h-4 w-4" /> Practise more
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---------- Question ----------
  const remaining = TIME_LIMIT - elapsed;
  const timeLow = mode === "TIMED" && remaining <= 60;
  const progress = ((idx + (submitted ? 1 : 0)) / questions.length) * 100;

  const optionState = (id) => {
    if (!submitted) return selected.includes(id) ? "selected" : "default";
    if (correctSet.includes(id)) return "correct";
    if (selected.includes(id)) return "incorrect";
    return "default";
  };

  return (
    <div>
      {/* Sticky session bar */}
      <div className="sticky top-16 z-40 border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-3">
          <div className="flex-1">
            <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="question-progress">
                Question {idx + 1} of {questions.length}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-green-600 dark:text-green-400">{tally.correct} correct</span>
                <span className="text-red-500">{tally.incorrect} wrong</span>
              </span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
          <div
            data-testid="session-timer"
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono-num text-sm font-semibold",
              timeLow
                ? "border-red-500/40 bg-red-500/10 text-red-500"
                : "border-border text-foreground"
            )}
          >
            <Timer className="h-4 w-4" />
            {mode === "TIMED" ? fmtTime(Math.max(0, remaining)) : fmtTime(elapsed)}
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="exit-session-btn"
            onClick={handleExit}
            className="gap-1.5 text-muted-foreground"
          >
            <DoorOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Exit</span>
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* Meta */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="rounded-full border-border font-medium">
                {EXAM_LABELS[current.examType] || current.examType}
              </Badge>
              <span className={cn("text-xs font-semibold uppercase tracking-wide", subjectAccent(current.subject))}>
                {SUBJECT_LABELS[current.subject] || current.subject}
              </span>
              <Badge className={cn("rounded-full border", difficultyClass(current.difficulty))}>
                {DIFFICULTY_LABELS[current.difficulty]}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {current.chapter} · {current.year}
              </span>
              {isMultiple && (
                <Badge variant="secondary" className="rounded-full text-[10px]">
                  Select all that apply
                </Badge>
              )}
              <BookmarkButton
                questionId={current.id}
                active={bookmarks.has(current.id)}
                onChange={toggleBookmark}
                size="sm"
                className="ml-auto"
              />
            </div>

            {/* Question */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <QuestionContent
                question={current}
                data-testid="question-text"
                className="text-lg leading-relaxed"
              />
            </div>

            {/* Options */}
            <div className="mt-4 space-y-3">
              {current.options.map((opt) => {
                const state = optionState(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={submitted}
                    data-testid={`mcq-option-${opt.id}`}
                    onClick={() => toggleOption(opt.id)}
                    className={cn(
                      "flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors min-h-[56px]",
                      state === "default" &&
                        "border-border bg-card hover:border-primary/50 hover:bg-secondary/50",
                      state === "selected" && "border-primary bg-primary/5",
                      state === "correct" &&
                        "border-green-500 bg-green-50 dark:bg-green-950/40",
                      state === "incorrect" && "border-red-500 bg-red-50 dark:bg-red-950/40",
                      !submitted && "cursor-pointer"
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border text-sm font-bold",
                        state === "default" && "border-border bg-secondary text-muted-foreground",
                        state === "selected" && "border-primary bg-primary text-primary-foreground",
                        state === "correct" && "border-green-500 bg-green-500 text-white",
                        state === "incorrect" && "border-red-500 bg-red-500 text-white"
                      )}
                    >
                      {LETTERS[opt.id] || opt.id.toUpperCase()}
                    </span>
                    <MathText className="flex-1 text-[15px]" text={opt.text} />
                    {state === "correct" && <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-500" />}
                    {state === "incorrect" && <XCircle className="h-5 w-5 flex-shrink-0 text-red-500" />}
                  </button>
                );
              })}
            </div>

            {/* Action */}
            {!submitted ? (
              <Button
                data-testid="submit-answer-btn"
                onClick={handleSubmit}
                className="mt-5 w-full rounded-xl py-6 text-base"
              >
                Submit answer
              </Button>
            ) : (
              <>
                <div
                  data-testid="feedback-banner"
                  className={cn(
                    "mt-5 flex items-center gap-3 rounded-xl border p-4",
                    wasCorrect
                      ? "border-green-500/40 bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400 animate-fade-up"
                      : "border-red-500/40 bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400 animate-shake"
                  )}
                >
                  {wasCorrect ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                  <span className="font-semibold">
                    {wasCorrect ? "Correct!" : "Not quite — check the solution below."}
                  </span>
                </div>

                {/* AI solution box */}
                <div
                  data-testid="solution-box"
                  className="relative mt-5 overflow-hidden rounded-2xl border border-primary/25 bg-card p-6"
                  style={{ boxShadow: "0 0 0 1px hsl(9 86% 58% / 0.08), 0 8px 40px -12px hsl(9 86% 58% / 0.25)" }}
                >
                  <div className="mb-4 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <Sparkles className="h-4 w-4 text-primary" /> Step-by-step solution
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="regenerate-solution-btn"
                      onClick={regenerate}
                      disabled={regenLoading}
                      className="gap-1.5 text-xs text-primary hover:bg-primary/10 hover:text-primary"
                    >
                      {regenLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                      Regenerate with AI
                    </Button>
                  </div>
                  <ol className="space-y-3" aria-live="polite">
                    {current.solution?.steps?.map((step, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-bold text-muted-foreground">
                          {i + 1}
                        </span>
                        <MathText className="text-[15px] leading-relaxed" text={step} />
                      </li>
                    ))}
                  </ol>
                  {current.solution?.finalAnswer && (
                    <div className="mt-4 rounded-xl bg-secondary/60 p-4">
                      <span className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        Final answer
                      </span>
                      <MathText block className="mt-1 text-[15px] font-medium" text={current.solution.finalAnswer} />
                    </div>
                  )}

                  <SolutionVideo questionId={current.id} />
                  <QuestionFeedback questionId={current.id} />
                </div>

                <Button
                  data-testid="next-question-btn"
                  onClick={handleNext}
                  className="mt-5 w-full rounded-xl py-6 text-base"
                >
                  {idx + 1 >= questions.length ? "Finish session" : "Next question"}
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
